import {
    useMemo,
    type FC
} from "react";
import {
    Pressable,
    Animated,
    Platform
} from "react-native";
import type IFieldActionProps from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    getAppearStyle,
    usePressScale,
    usePresence
} from "../../../../helpers/motion";
import {
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    FieldActionContext
} from "../../context";
import fieldStylesheet, {
    getFieldActionIconColors
} from "../../stylesheet";
import StateLayer from "../../../stateLayer";

const preventActionFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const FieldAction: FC<IFieldActionProps> = ({
    icon: IconComponentProp,
    accessibilityLabel,
    customIconColor,
    currentColors,
    currentSize,
    customTheme,
    isMultiline,
    controlsID,
    isDisabled,
    isPressed,
    isVisible,
    onPress,
    style
}) => {
    const {
        shapeConfigs,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        isPressed: isPressing,
        interactionHandlers,
        isFocusVisible,
        isHovered
    } = useInteractionState({
        isDisabled
    });

    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        isVisible: isVisible ?? true,
        enterIntent: "iconAppear",
        exitIntent: "iconAppear",
        customTheme
    });

    const pressScaleStyle = usePressScale({
        isPressed: isPressing,
        customTheme,
        isDisabled
    });

    const appearStyle = useMemo(() => getAppearStyle({
        isReduceMotionEnabled,
        progress
    }), [
        isReduceMotionEnabled,
        progress
    ]);

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const currentIconColors = getFieldActionIconColors({
        isHighlighted: isHovered || isPressing,
        customIconColor,
        currentColors,
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible,
        shapeConfigs,
        currentSize,
        isMultiline,
        radiuses,
        borders,
        colors
    });

    const webProps = Platform.OS === "web" ? {
        onMouseDown: preventActionFocusSteal,
        "aria-controls": controlsID,
        "aria-pressed": isPressed
    } : {};

    const actionContext = useMemo(() => ({
        isPressed: !!isPressed
    }), [
        isPressed
    ]);

    if(!isRendered) {
        return null;
    }

    return <AnimatedPressable
        {...webProps}
        {...interactionHandlers}
        style={[
            containerStyle,
            fieldStylesheet.actionContainer,
            containerDynamicStyle,
            isVisible === false ? fieldStylesheet.hiddenActionContainer : undefined,
            isVisible === undefined ? undefined : appearStyle
        ]}
        aria-checked={Platform.OS === "web" ? undefined : isPressed}
        aria-hidden={isVisible === false ? true : undefined}
        accessibilityLabel={accessibilityLabel}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onPress={onPress}
        role="button"
    >
        <Animated.View
            style={[
                surfaceStyle,
                fieldStylesheet.actionSurface,
                surfaceDynamicStyle,
                pressScaleStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.xs}
                customTheme={customTheme}
                isPressed={isPressing}
                overlayColor="default"
                isHovered={isHovered}
            />
            <FieldActionContext.Provider
                value={actionContext}
            >
                <IconComponentProp
                    customColor={currentIconColors.iconColor}
                    color={currentIconColors.iconColorKey}
                    size={currentSize.iconSize}
                />
            </FieldActionContext.Provider>
        </Animated.View>
    </AnimatedPressable>;
};
export default FieldAction;
